import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const UnderDevelopment = ({ 
  title, 
  location = null, 
  service = null,
  metaDescription,
  pageType
}) => {
  // Determine if this is a location or service page
  const isLocation = pageType === 'location';
  const isService = pageType === 'service';
  
  // Create appropriate title based on page type
  const pageTitle = isLocation 
    ? `ECU Remapping ${location} | Coming Soon | Gatwick Tuning` 
    : isService 
      ? `${service} | Service Coming Soon | Gatwick Tuning` 
      : title;
  
  // Create appropriate meta description
  const pageDescription = metaDescription || (
    isLocation 
      ? `Professional ECU remapping services in ${location} coming soon. Contact Gatwick Tuning for immediate assistance with your vehicle tuning needs.`
      : isService
        ? `Our ${service.toLowerCase()} service page is under development. Contact us today for information about this service.`
        : "This page is currently under development. Please check back soon for updates."
  );

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        {isLocation && <meta name="keywords" content={`ECU remapping ${location}, car tuning ${location}, vehicle remapping near ${location}, diesel tuning ${location}, mobile car tuning ${location}`} />}
        {isService && <meta name="keywords" content={`${service}, ECU remapping, car tuning, professional ${service.toLowerCase()}, Gatwick tuning services`} />}
      </Helmet>

      <main className="pt-20 min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="relative bg-primary" aria-labelledby="under-development-heading">
          <div className="max-w-7xl mx-auto py-24 px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 id="under-development-heading" className="text-4xl tracking-tight font-extrabold text-white sm:text-5xl md:text-6xl">
                {isLocation ? (
                  <>
                    <span className="block">ECU Remapping for</span>
                    <span className="block text-accent">{location}</span>
                  </>
                ) : isService ? (
                  <>
                    <span className="block">{service}</span>
                    <span className="block text-accent">Coming Soon</span>
                  </>
                ) : (
                  <span className="block">Coming Soon</span>
                )}
              </h1>
              <p className="mt-4 text-xl text-gray-300 max-w-3xl mx-auto">
                {isLocation 
                  ? `We're preparing our dedicated ${location} ECU remapping service page with local information and customer testimonials. It will be available soon.`
                  : isService
                    ? `We're building a comprehensive guide to our ${service.toLowerCase()} services. This page will be available soon.`
                    : "We're currently building this page to provide you with the best possible information and service."}
              </p>
            </div>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="prose prose-lg mx-auto">
              <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">
                {isLocation 
                  ? `ECU Remapping Services in ${location}`
                  : isService
                    ? `Our ${service} Service`
                    : "This Page is Under Development"}
              </h2>
              
              <p>
                {isLocation 
                  ? `Our ${location} ECU remapping service page is under development. While we prepare this page, we're still actively providing our full range of professional vehicle tuning services in ${location} and surrounding areas.`
                  : isService
                    ? `We're currently building a comprehensive page about our ${service.toLowerCase()} services to provide you with detailed information, process explanations, and pricing options.`
                    : "We're working hard to bring you the content you're looking for. This page is currently under development."}
              </p>
              
              <p>
                In the meantime, you can:
              </p>
              
              <ul className="mt-4">
                <li className="mb-2">
                  <Link to="/contact" className="text-accent hover:underline">
                    Contact us directly
                  </Link> for immediate assistance with your tuning needs.
                </li>
                <li className="mb-2">
                  Visit our <Link to="/services" className="text-accent hover:underline">
                    services overview page
                  </Link> to learn about our ECU remapping options.
                </li>
                <li className="mb-2">
                  Check our <Link to="/blog" className="text-accent hover:underline">
                    latest blog posts
                  </Link> for information about vehicle tuning.
                </li>
                <li className="mb-2">
                  View our <Link to="/pricing" className="text-accent hover:underline">
                    pricing page
                  </Link> for our current service rates.
                </li>
              </ul>

              {isLocation && (
                <div className="bg-gray-100 p-6 rounded-lg mt-8">
                  <h3 className="text-xl font-semibold text-primary mb-3">Mobile Service Available in {location}</h3>
                  <p className="text-gray-700">
                    Can't wait for the full page? We offer a convenient mobile ECU remapping service throughout {location} and surrounding areas. Our technicians can come to your home or workplace for maximum convenience.
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-primary">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h2 className="text-3xl font-extrabold text-white sm:text-4xl">
                Need Assistance Right Away?
              </h2>
              <p className="mt-4 text-lg text-gray-300">
                {isLocation 
                  ? `Don't wait for our ${location} page to be complete. Contact our team today for ECU remapping services in ${location}.`
                  : isService
                    ? `Want to know more about our ${service.toLowerCase()} services now? Get in touch with our expert team.`
                    : "Don't wait for this page to be complete. Contact our team today for immediate assistance."}
              </p>
              <div className="mt-8">
                <Link to="/contact" className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-primary bg-white hover:bg-gray-50">
                  Contact Our Team
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default UnderDevelopment; 